import React, { useEffect, useMemo, useState } from "react";
import "./GuesthouseBookingStatus.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const LEGACY_PORTAL_BASE = (process.env.REACT_APP_LEGACY_PORTAL_BASE || "http://127.0.0.1").replace(/\/$/, "");
const ALLOWED_MODULES = [1, 2, 5, 10, 13, 15, 17, 19, 21, 25];
const EMPLOYEE_MODULES = [1, 2, 5, 13, 15, 17, 19, 21];
const LETTER_MODULES = [1, 2, 5, 13, 15, 17, 19, 21];

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit"
  });
}

function displayRoomType(roomType) {
  if (roomType === "R") return "Room";
  if (roomType === "S") return "Suite";
  if (roomType === "H") return "Seminar Hall";
  return roomType || "";
}

export default function GuesthouseBookingStatus() {
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(user?.moduleId, 10);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [adminMobile, setAdminMobile] = useState("");
  const [referenceUsers, setReferenceUsers] = useState([]);
  const [referenceID, setReferenceID] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) {
      setError("Unauthorized access Or Session Timed out.");
      return;
    }

    // Admin modules keep using their existing flow; this page currently loads employee-equivalent list.
    if ([10, 25].includes(moduleId)) {
      return;
    }

    const fetchStatus = async () => {
      try {
        setLoading(true);
        setError("");

        const q = new URLSearchParams({
          moduleId: String(moduleId || ""),
          userId: String(user?.id || ""),
          deptId: String(user?.deptid || ""),
          mobile: String(user?.mobile || "")
        });

        const res = await fetch(`${API_ROOT}/api/guesthouse/booking/status?${q.toString()}`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch booking details");
        }

        setRows(Array.isArray(data) ? data : []);
        // Debug: log the keys of the first row to check field names
        if (Array.isArray(data) && data.length > 0) {
          console.log('Booking row keys:', Object.keys(data[0]));
        }
      } catch (err) {
        setRows([]);
        setError(err.message || "Failed to fetch booking details");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [moduleId, user?.id, user?.deptid, user?.mobile]);

  const viewAttachment = async (supportDoc) => {
    if (!supportDoc) {
      window.alert("No attachment found.");
      return;
    }

    const rawPath = String(supportDoc || "").trim();
    if (!rawPath) {
      window.alert("No attachment found.");
      return;
    }

    const normalizedRaw = rawPath.replace(/\\/g, "/");

    // Handle plain file names and absolute guesthouse upload paths via the API route.
    if (!normalizedRaw.includes("/") || /\/uploads\/ghbooking_request\//i.test(normalizedRaw)) {
      const fileName = normalizedRaw.split("/").pop();
      if (!fileName) {
        window.alert("Attachment URL could not be resolved.");
        return;
      }
      const url = `${API_ROOT}/api/guesthouse/ghbooking_request/${encodeURIComponent(fileName)}`;
      window.open(url, "_blank");
      return;
    }

    // Otherwise, fallback to old logic
    const normalizedPath = rawPath.replace(/\\/g, "/").replace(/^\/+/, "");
    const encodedPath = normalizedPath.split("/").map(encodeURIComponent).join("/");

    if (/^https?:\/\//i.test(rawPath)) {
      window.location.href = rawPath;
      return;
    }

    const candidates = [
      API_ROOT ? `${API_ROOT}/${encodedPath}` : "",
      LEGACY_PORTAL_BASE ? `${LEGACY_PORTAL_BASE}/${encodedPath}` : ""
    ].filter(Boolean);

    for (const candidate of candidates) {
      try {
        const res = await fetch(candidate, { method: "HEAD" });
        if (res.ok) {
          window.location.href = candidate;
          return;
        }
      } catch {
        // Ignore probe errors and try next candidate.
      }
    }

    if (candidates.length) {
      window.location.href = candidates[0];
      return;
    }

    window.alert("Attachment URL could not be resolved.");
  };

  const openPrintForm = (bookingId) => {
    const q = new URLSearchParams({
      moduleId: String(moduleId || ""),
      userId: String(user?.id || ""),
      deptId: String(user?.deptid || "")
    });
    const printUrl = `${API_ROOT}/api/guesthouse/booking/print/${encodeURIComponent(bookingId)}?${q.toString()}`;
    window.open(printUrl, "_blank", "noopener,noreferrer");
  };

  const openLetter = (bookingId, bookingRef) => {
    const q = new URLSearchParams({ moduleId: String(moduleId || "") });
    if (bookingRef) q.set("bookingRef", bookingRef);
    window.open(`${API_ROOT}/api/guesthouse/booking/invitation-letter/${encodeURIComponent(bookingId)}?${q}`, "_blank", "noopener,noreferrer");
  };

  const openEdit = (bookingId) => {
    if ([1, 13, 15, 17, 21].includes(moduleId)) {
      window.open(
        `/guesthouse/booking-edit/${encodeURIComponent(bookingId)}`,
        "_blank",
        "noopener,noreferrer,width=1000,height=600"
      );
      return;
    }
    const editUrl = `${LEGACY_PORTAL_BASE}/ChangeRequestbyEmp.php?BookingID=${encodeURIComponent(bookingId)}`;
    window.open(editUrl, "_blank", "noopener,noreferrer,width=1000,height=600");
  };

  const fetchReferenceUsers = async () => {
    if (!/^\d{10}$/.test(adminMobile)) {
      setError("Enter valid 10-digit registered reference mobile.");
      setReferenceUsers([]);
      setReferenceID("");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const q = new URLSearchParams({
        moduleId: String(moduleId || ""),
        mobile: adminMobile
      });

      const res = await fetch(`${API_ROOT}/api/guesthouse/booking/reference-users?${q.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch reference users");
      }

      const list = Array.isArray(data) ? data : [];
      setReferenceUsers(list);
      setReferenceID(list[0]?.ReferenceID || "");
    } catch (err) {
      setReferenceUsers([]);
      setReferenceID("");
      setError(err.message || "Failed to fetch reference users");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminLedger = async () => {
    if (!referenceID) {
      setError("Please select a Reference ID.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const q = new URLSearchParams({
        moduleId: String(moduleId || ""),
        referenceID
      });
      if (selectedDate) {
        q.set("selectedDate", selectedDate);
      }

      const res = await fetch(`${API_ROOT}/api/guesthouse/booking/admin-ledger?${q.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to load booking status");
      }

      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to load booking status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="container-fluid">
      <h3 className="ctitle">Booking Details</h3>

      {EMPLOYEE_MODULES.includes(moduleId) && (
        <div style={{ textAlign: "left", marginTop: "10px", marginBottom: "10px" }}>
          <a
            href="/guesthouse/booking-request"
            className="btn btn-primary"
            style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
          >
            New Booking Request
          </a>
        </div>
      )}

      {[10, 25].includes(moduleId) && (
        <>
          <div style={{ textAlign: "left", marginTop: "10px", marginBottom: "10px" }}>
            <a
              href="/payment-details"
              className="btn btn-primary"
              style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
            >
              Payment Details
            </a>
          </div>

          <div className="row g-3 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label">Registered Ref. Mobile</label>
              <input
                className="form-control"
                type="text"
                value={adminMobile}
                onChange={(e) => setAdminMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="Mobile...*"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Registered Reference User</label>
              <select
                className="form-control"
                value={referenceID}
                onChange={(e) => setReferenceID(e.target.value)}
              >
                <option value="">Select Name</option>
                {referenceUsers.map((item) => (
                  <option key={item.ReferenceID} value={item.ReferenceID}>
                    {`${item.ReferenceName} (${item.ReferenceID})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label">Select Date</label>
              <input
                className="form-control"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            <div className="col-md-2 d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={fetchReferenceUsers}
                disabled={loading}
              >
                Fetch User
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={fetchAdminLedger}
                disabled={loading}
              >
                View
              </button>
            </div>
          </div>
        </>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle guesthouse-booking-status-table">
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>BookDate</th>
                <th>VisitorDetails</th>
                <th>Status</th>
                <th>Visit</th>
                <th>GuestHouse</th>
                <th>Type</th>
                <th>CheckIn</th>
                <th>CheckOut</th>
                <th>Ref.ID</th>
                <th>Print</th>
                <th>Doc</th>
                <th>Edit</th>
                {LETTER_MODULES.includes(moduleId) && <th>Letter</th>}
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={LETTER_MODULES.includes(moduleId) ? 14 : 13} className="text-center text-muted">No records found.</td>
                </tr>
              )}

              {rows.map((row, idx) => (
                <tr key={row.BookingID}>
                  <td>{idx + 1}</td>
                  <td>{formatDate(row.BookingDate)}</td>
                  <td>
                    <span style={{ color: row.Status === "Booked" ? "green" : row.Status === "Pending" ? "saddlebrown" : undefined }}>
                      {(() => {
                        let details = row.VisitorName || "";
                        if (row.Mobile || row.Relationship) {
                          details += ` (${row.Mobile || "-"}${row.Relationship ? ", " + row.Relationship : ""})`;
                        }
                        return details;
                      })()}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: row.Status === "Booked" ? "green" : row.Status === "Pending" ? "saddlebrown" : undefined }}>
                      {row.Status || ""}
                    </span>
                  </td>
                  <td>{row.TypeOfVisit === "P" ? "Personal" : row.TypeOfVisit === "O" ? "Official" : row.TypeOfVisit || ""}</td>
                  <td>{row.GuestHouseAbb || row.GuestHouse || ""}</td>
                  <td>{displayRoomType(row.RoomType)}</td>
                  <td>{formatDate(row.FromDate)}</td>
                  <td>{formatDate(row.ToDate)}</td>
                  <td>{row.BookingID}</td>
                  <td>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openPrintForm(row.BookingID); }}>Form</a>
                  </td>
                  <td>
                    {row.SupportDoc ? (
                      <>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); viewAttachment(row.SupportDoc); }}>Doc</a>
                      </>
                    ) : "—"}
                  </td>
                  <td>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openEdit(row.BookingID); }}>Edit</a>
                  </td>
                  {LETTER_MODULES.includes(moduleId) && (
                    <td>
                      {(row.GuestHouseID === 4 || row.GuestHouseID === 5) && row.Status === 'Booked' ? (
                        <>
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openLetter(row.BookingID, null); }}>Letter</a>
                        </>
                      ) : "—"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
